import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const searchPluginKey = new PluginKey("searchAndReplace");

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const findResults = (doc, searchTerm) => {
  if (!searchTerm) return [];

  let textContent = "";
  const positions = [];

  doc.descendants((node, pos) => {
    if (node.isText) {
      for (let i = 0; i < node.text.length; i++) {
        positions[textContent.length] = pos + i;
        textContent += node.text[i];
      }
    } else if (node.isBlock && textContent.length && !textContent.endsWith("\n")) {
      positions[textContent.length] = pos;
      textContent += "\n";
    }
  });

  const results = [];
  const regex = new RegExp(escapeRegExp(searchTerm), "gi");
  let match = regex.exec(textContent);
  while (match !== null) {
    if (match[0].length === 0) {
      regex.lastIndex++;
    } else {
      const start = match.index;
      const end = start + match[0].length;
      results.push({ from: positions[start], to: positions[end - 1] + 1 });
    }
    match = regex.exec(textContent);
  }

  return results;
};

const SearchAndReplace = Extension.create({
  name: "searchAndReplace",

  addOptions() {
    return {
      searchResultClass: "search-result",
      searchResultCurrentClass: "search-result-current",
    };
  },

  addStorage() {
    return {
      searchTerm: "",
      replaceTerm: "",
      results: [],
      currentIndex: 0,
    };
  },

  onUpdate() {
    const storage = this.editor.storage.searchAndReplace;
    if (!storage.searchTerm) return;

    const { state, view } = this.editor;
    storage.results = findResults(state.doc, storage.searchTerm);
    storage.currentIndex = Math.max(
      0,
      Math.min(storage.currentIndex, storage.results.length - 1),
    );
    view.dispatch(state.tr.setMeta(searchPluginKey, true));
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        key: searchPluginKey,
        props: {
          decorations(state) {
            const { results, currentIndex } = extension.editor.storage.searchAndReplace;
            if (!results.length) return DecorationSet.empty;

            const max = state.doc.content.size;
            const decorations = [];
            results.forEach((result, index) => {
              if (result.from >= result.to || result.to > max) return;

              decorations.push(
                Decoration.inline(result.from, result.to, {
                  class:
                    index === currentIndex ?
                      extension.options.searchResultCurrentClass :
                      extension.options.searchResultClass,
                }),
              );
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setSearchTerm:
        (term) =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          storage.searchTerm = term;
          storage.results = findResults(editor.state.doc, term);
          storage.currentIndex = 0;
          if (dispatch) dispatch(tr.setMeta(searchPluginKey, true));
          return true;
        },

      setReplaceTerm:
        (term) =>
        ({ editor }) => {
          editor.storage.searchAndReplace.replaceTerm = term;
          return true;
        },

      nextMatch:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          if (!storage.results.length) return false;

          storage.currentIndex =
            (storage.currentIndex + 1) % storage.results.length;
          if (dispatch) dispatch(tr.setMeta(searchPluginKey, true));
          return true;
        },

      previousMatch:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          if (!storage.results.length) return false;

          storage.currentIndex =
            (storage.currentIndex - 1 + storage.results.length) %
            storage.results.length;
          if (dispatch) dispatch(tr.setMeta(searchPluginKey, true));
          return true;
        },

      replaceCurrent:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          const result = storage.results[storage.currentIndex];
          if (!result) return false;

          if (dispatch) {
            tr.insertText(storage.replaceTerm, result.from, result.to);
            dispatch(tr);
          }
          return true;
        },

      replaceAll:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          if (!storage.results.length) return false;

          if (dispatch) {
            [...storage.results]
              .sort((a, b) => b.from - a.from)
              .forEach((result) => {
                tr.insertText(storage.replaceTerm, result.from, result.to);
              });
            dispatch(tr);
          }
          return true;
        },

      clearSearch:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = editor.storage.searchAndReplace;
          storage.searchTerm = "";
          storage.replaceTerm = "";
          storage.results = [];
          storage.currentIndex = 0;
          if (dispatch) dispatch(tr.setMeta(searchPluginKey, true));
          return true;
        },
    };
  },
});

export default SearchAndReplace;
